
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GuideHeroSection from "@/components/blog/GuideHeroSection";
import DataSheetDownloadSection from "@/components/blog/DataSheetDownloadSection";
import XDRIntroductionSection from "@/components/blog/XDRIntroductionSection";
import AIXDRDifferenceSection from "@/components/blog/AIXDRDifferenceSection";
import XDRBenefitsSection from "@/components/blog/XDRBenefitsSection";
import XDRUseCasesSection from "@/components/blog/XDRUseCasesSection";
import XDRKeyFeaturesSection from "@/components/blog/XDRKeyFeaturesSection";
import XDRChallengesSection from "@/components/blog/XDRChallengesSection";
import XDRImplementationSection from "@/components/blog/XDRImplementationSection";
import RelatedResourcesSection from "@/components/blog/RelatedResourcesSection";
import GuideCTASection from "@/components/blog/GuideCTASection";
import TableOfContents from "@/components/seo/TableOfContents";
import FAQSchema from "@/components/seo/FAQSchema";

const UltimateGuideAIXDR = () => {
  const guideFAQs = [
    {
      question: "What is the difference between XDR and SIEM?",
      answer: "XDR provides unified threat detection across endpoints, networks, and cloud environments with automated response capabilities, while SIEM primarily focuses on log analysis and correlation. XDR offers deeper integration and autonomous investigation features."
    },
    {
      question: "How long does XDR implementation typically take?",
      answer: "Modern AI-powered XDR platforms like Spharaka can be deployed in hours to days, compared to traditional SIEM implementations that can take months. Cloud-native architectures and intelligent onboarding significantly reduce deployment time."
    },
    {
      question: "What ROI can organizations expect from XDR?",
      answer: "Organizations typically see 300-500% ROI within the first year through reduced security incidents, faster threat response times, decreased staffing requirements, and improved compliance posture."
    },
    {
      question: "Who can benefit from XDR?",
      answer: "XDR platforms are designed for organizations of all sizes - from small and medium businesses to large enterprises and government agencies. Cloud-native solutions offer flexible deployment models and pricing that make advanced security accessible across all organizational types."
    }
  ];

  React.useEffect(() => {
    // SEO Meta updates
    document.title = "AI-Powered XDR Strategy: Redefining Cybersecurity Leadership";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'Transform your cybersecurity strategy with AI-powered XDR. Expert insights on implementation, ROI, and strategic leadership for modern threat detection.');
    document.head.appendChild(metaDescription);

    // Add structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "AI-Powered XDR Strategy: Redefining Cybersecurity Leadership",
      "author": {
        "@type": "Organization",
        "name": "Spharaka"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Spharaka"
      },
      "datePublished": new Date().toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      }
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.textContent = JSON.stringify(structuredData);
    document.head.appendChild(scriptTag);

    return () => {
      // Cleanup
      document.head.removeChild(metaDescription);
      document.head.removeChild(scriptTag);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <FAQSchema faqs={guideFAQs} title="AI-Powered XDR Guide FAQ" />
      <Navbar />
      <main className="flex-1">
        <GuideHeroSection />
        
        {/* Table of Contents */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <TableOfContents className="max-w-4xl mx-auto" />
          </div>
        </section>
        
        <DataSheetDownloadSection />
        <XDRIntroductionSection />
        
        {/* Main Content Sections */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <AIXDRDifferenceSection />
            <XDRBenefitsSection />
            <XDRUseCasesSection />
            <XDRKeyFeaturesSection />
            <XDRChallengesSection />
            <XDRImplementationSection />
          </div>
        </section>

        <RelatedResourcesSection />
        <GuideCTASection />
      </main>
      <Footer />
    </div>
  );
};

export default UltimateGuideAIXDR;
